// Picking what one person sees in What's new (docs/plans/whats-new.md): by business type, role
// and situation. Entries for another kind of business are left out, never greyed.
import type {
  WhatsNewEntry,
  WhatsNewFix,
  WhatsNewFor,
  WhatsNewRelease,
  WhatsNewRole,
} from '../whats-new.ts'

const RANK: Record<WhatsNewRole, number> = {
  viewer: 0,
  waiter: 1,
  staff: 2,
  manager: 3,
  admin: 4,
  owner: 5,
}

export type Audience = 'restaurant' | 'workshop' | 'shops'

export interface Viewer {
  /** Restaurant mode (`useBusinessMode().restaurant`). */
  restaurant: boolean
  businessType: string | null
  role: WhatsNewRole
  /** How many businesses this account has. */
  businesses: number
}

export function audienceOf(viewer: Pick<Viewer, 'restaurant' | 'businessType'>): Audience {
  if (viewer.restaurant) return 'restaurant'
  return viewer.businessType === 'workshop' ? 'workshop' : 'shops'
}

/** "For your …": the word for the viewer's kind of business. */
export const AUDIENCE_WORD: Record<Audience, string> = {
  restaurant: 'restaurant',
  workshop: 'workshop',
  shops: 'shop',
}

/** "…, a kirana shop, …": the business type in a sentence. */
export function kindWords(viewer: Pick<Viewer, 'restaurant' | 'businessType'>): string {
  if (viewer.restaurant) return 'restaurant'
  const words: Record<string, string> = {
    kirana: 'kirana shop',
    pharmacy: 'pharmacy',
    hardware: 'hardware shop',
    apparel: 'clothing shop',
    electronics: 'electronics shop',
    cosmetics: 'cosmetics shop',
    bakery: 'bakery',
    wholesale: 'wholesale business',
    workshop: 'workshop',
    agrovet: 'agrovet',
    restaurant: 'restaurant',
  }
  return words[viewer.businessType ?? ''] ?? 'shop'
}

function matches(item: { for: WhatsNewFor[] }, viewer: Viewer, audience: Audience): boolean {
  return item.for.some(
    (f) => f === 'all' || f === audience || (!viewer.restaurant && f === viewer.businessType),
  )
}

function allowed(item: WhatsNewEntry | WhatsNewFix, viewer: Viewer): boolean {
  if (RANK[viewer.role] < RANK[item.minRole]) return false
  if (item.when === 'several-businesses' && viewer.businesses < 2) return false
  return true
}

export interface Picked {
  /** For the viewer's own kind of business, the first one shown large. */
  mine: WhatsNewEntry[]
  /** For every business. */
  everyone: WhatsNewEntry[]
  fixed: WhatsNewFix[]
}

export function entriesFor(release: WhatsNewRelease, viewer: Viewer): Picked {
  const audience = audienceOf(viewer)
  const shown = release.entries.filter((e) => allowed(e, viewer) && matches(e, viewer, audience))
  return {
    mine: shown.filter((e) => !e.for.includes('all')),
    everyone: shown.filter((e) => e.for.includes('all')),
    fixed: release.fixed.filter((f) => allowed(f, viewer) && matches(f, viewer, audience)),
  }
}

export const pickedCount = (p: Picked) => p.mine.length + p.everyone.length + p.fixed.length

/**
 * Releases people may read. Live shows only released ones (a date); staging and a local
 * build also show the coming one, so the owner can read it before `/release`.
 */
export function releasedFor(releases: WhatsNewRelease[], apiUrl: string): WhatsNewRelease[] {
  const live = /^https:\/\/api\.sanogodam\.com\/?$/.test(apiUrl)
  return releases.filter((r) => r.date !== null || !live)
}

/** The dashboard card waits 30 days at most after a release. */
export const CARD_DAYS = 30

export function cardRelease(
  releases: WhatsNewRelease[],
  seen: string | null,
  viewer: Viewer,
  today: string,
): WhatsNewRelease | null {
  const newest = releases[0]
  if (!newest || newest.id === seen) return null
  if (newest.date) {
    const age = (Date.parse(today) - Date.parse(newest.date)) / 86_400_000
    if (age > CARD_DAYS) return null
  }
  return pickedCount(entriesFor(newest, viewer)) > 0 ? newest : null
}

// What this person last opened or dismissed, per device. A hint only: never synced.
const seenKey = (userId: string) => `ims.whatsnew.${userId}`
const listeners = new Set<() => void>()

export function readSeen(userId: string): string | null {
  try {
    return localStorage.getItem(seenKey(userId))
  } catch {
    return null
  }
}

export function markSeen(userId: string, releaseId: string): void {
  try {
    if (localStorage.getItem(seenKey(userId)) === releaseId) return
    localStorage.setItem(seenKey(userId), releaseId)
  } catch {
    // Private mode: it shows again next time.
  }
  listeners.forEach((l) => l())
}

export function subscribeSeen(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The newest release this device has run, for the toast after an update. */
const DEVICE_KEY = 'ims.whatsnew.device'

/** True once per new release on a device that ran an older one; a first install stays quiet. */
export function updatedSinceLastRun(newestId: string): boolean {
  try {
    const last = localStorage.getItem(DEVICE_KEY)
    if (last === newestId) return false
    localStorage.setItem(DEVICE_KEY, newestId)
    return last !== null
  } catch {
    return false
  }
}
